import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ConfigKey, MprolabContract } from '@mprolab/sdk'
import { useAzureConfigService } from 'hooks/useAzureConfigService'
import { useLazyGetConfigUpdateDataQuery, useLazyGetContractsQuery } from 'state/services/config/config'
import dayjs from 'dayjs'
import { AppDispatch, AppState } from '../index'
import {
  setMprolabContractsLastModifiedDate,
  setRestrictedUserNames,
  setRestrictedNamesLastModifiedDate
} from './actions'
import { GlobalState } from './reducer'

let configUpdateUnavailableLogged = false

export function useAzureSettings() {
  const dispatch = useDispatch<AppDispatch>()
  const { getAzureResponse } = useAzureConfigService()
  const restrictedNames = useSelector<AppState, AppState['persistedReducer']['global']['restrictedNames']>((state) => {
    return state.persistedReducer.global.restrictedNames
  })

  const { restrictedNamesLastModifiedDate } = useModifiedDates()

  const getRestrictedNames = async () => {
    try {
      const restrictedNames = await getAzureResponse<string[]>('restricted-names')
      if (!dayjs(restrictedNames?.lastModified).isSame(restrictedNamesLastModifiedDate)) {
        dispatch(setRestrictedUserNames(restrictedNames.value))
        dispatch(setRestrictedNamesLastModifiedDate(restrictedNames?.lastModified))
      }
    } catch (error) {
      dispatch(setRestrictedUserNames(restrictedNames))
    }
  }

  return { getRestrictedNames }
}

export function useGlobalState(): GlobalState {
  const globalState = useSelector<AppState, AppState['persistedReducer']['global']>((state) => {
    return state.persistedReducer.global
  })

  return globalState
}

export function useModifiedDates() {
  const mprolabContractsLastModifiedDate = useSelector<
    AppState,
    AppState['persistedReducer']['global']['mprolabContractsLastModifiedDate']
  >((state) => {
    return state.persistedReducer.global.mprolabContractsLastModifiedDate
  })
  const restrictedNamesLastModifiedDate = useSelector<
    AppState,
    AppState['persistedReducer']['global']['restrictedNamesLastModifiedDate']
  >((state) => {
    return state.persistedReducer.global.restrictedNamesLastModifiedDate
  })

  return {
    mprolabContractsLastModifiedDate,
    restrictedNamesLastModifiedDate
  }
}

export function useGlobalSettings(): void {
  const dispatch = useDispatch<AppDispatch>()
  const { mprolabContractsLastModifiedDate } = useModifiedDates()
  const mprolabContracts = useSelector<AppState, AppState['persistedReducer']['global']['mprolabContracts']>((state) => {
    return state.persistedReducer.global.mprolabContracts
  })
  const [getContracts] = useLazyGetContractsQuery()
  const [getConfigUpdateData] = useLazyGetConfigUpdateDataQuery()
  const { getRestrictedNames } = useAzureSettings()
  const hasLoadedContracts = Object.values(mprolabContracts).some((contracts) => contracts.length > 0)

  useEffect(() => {
    ;(async () => {
      try {
        try {
          const contractsLastModifiedDate = await getConfigUpdateData({
            configKey: ConfigKey.MPROLAB_CONTRACTS
          }).unwrap()
          if (!dayjs(contractsLastModifiedDate.updatedAt).isSame(mprolabContractsLastModifiedDate) || !hasLoadedContracts) {
            await getContracts(undefined).unwrap()

            dispatch(setMprolabContractsLastModifiedDate(contractsLastModifiedDate.updatedAt))
          }
        } catch (error) {
          if (!configUpdateUnavailableLogged) {
            configUpdateUnavailableLogged = true
            console.warn(
              '[mprolab] Config update for mprolab-contracts failed; loading contracts from /api/config/contracts.',
              error,
            )
          }
          try {
            await getContracts(undefined).unwrap()
          } catch {
            /* getContracts may fail without gateway; UI surfaces its own errors */
          }
        }
      } catch (error) {
        console.error(error, 'useGlobalSettings')
      }
      await getRestrictedNames()
    })()
  }, [])
}

export function useMproTokenContractAddress(): MprolabContract[] {
  return useSelector<AppState, MprolabContract[]>((state) => {
    return state.persistedReducer.global?.mprolabContracts?.mproContract ?? []
  })
}

export function useMproReferralContractAddress(): MprolabContract[] {
  return useSelector<AppState, MprolabContract[]>((state) => {
    return state.persistedReducer.global?.mprolabContracts?.mproReferralContract ?? []
  })
}

/**
 * Session tokens, indexable by a wallet address in any casing.
 *
 * The map is keyed by whatever `account` the connector handed us when the
 * signature was made, and connectors are not consistent: some return the
 * EIP-55 checksummed form, some lowercase. Twenty-two call sites index this
 * map with their own `account`, so a single casing mismatch presents a signed-in
 * member as signed out — no error, no retry, just their licences gone from a
 * page that then offered to sell them one. That is what happened to a holder
 * who wrote in saying "I can't see my licence".
 *
 * So the lookup is case-insensitive at the source rather than at each of the
 * twenty-two places that would otherwise each have to remember.
 */
export function useLifetimeTokens(): { [key: string]: string } {
  const tokens = useSelector<AppState, AppState['persistedReducer']['global']['lifetimeTokens']>((state) => {
    return state.persistedReducer.global.lifetimeTokens
  })

  return useMemo(() => {
    if (!tokens) return tokens
    return new Proxy(tokens, {
      get(target, key) {
        if (typeof key !== 'string' || key in target) return target[key as keyof typeof target]
        const wanted = key.toLowerCase()
        const hit = Object.keys(target).find((stored) => stored.toLowerCase() === wanted)
        return hit ? target[hit] : undefined
      }
    })
  }, [tokens])
}

export function useRestrictedNames(): string[] {
  const dispatch = useDispatch<AppDispatch>()
  const { getAzureResponse } = useAzureConfigService()
  const restrictedUserNames = useSelector<AppState, AppState['persistedReducer']['global']['restrictedNames']>(
    (state) => {
      return state.persistedReducer.global.restrictedNames
    }
  )

  useEffect(() => {
    const getRestrictedNames = async () => {
      try {
        const restrictedNames = (await getAzureResponse<string[]>('restricted-names')).value
        dispatch(setRestrictedUserNames(restrictedNames))
      } catch (error) {
        console.error(error)
      }
    }

    getRestrictedNames()
  }, [])

  return restrictedUserNames
}

export function useNodeOwnersContractsDetails(): MprolabContract[] {
  const contracts = useSelector<AppState, MprolabContract[]>((state) => {
    return state.persistedReducer.global?.mprolabContracts?.nodeOwnersContract ?? []
  })
  return useMemo(() => contracts.filter(({ isActive }) => isActive), [contracts])
}

export function useMPROReferralContractsDetails(): MprolabContract[] {
  const contracts = useSelector<AppState, MprolabContract[]>((state) => {
    return state.persistedReducer.global?.mprolabContracts?.mproReferralContract ?? []
  })
  return useMemo(() => contracts.filter(({ isActive }) => isActive), [contracts])
}

export function useStakeContractAddress(): MprolabContract[] {
  return useSelector<AppState, MprolabContract[]>((state) => {
    return state.persistedReducer.global?.mprolabContracts?.mproStakeRewardContract ?? []
  })
}

export function useUniswapRouterAddress(): MprolabContract[] {
  const contracts = useSelector<AppState, MprolabContract[]>((state) => {
    return state.persistedReducer.global?.mprolabContracts?.uniswapRouterContract ?? []
  })
  return useMemo(() => contracts.filter(({ isActive }) => isActive), [contracts])
}

export function useUSDCContractAddress(): MprolabContract[] {
  const contracts = useSelector<AppState, MprolabContract[]>((state) => {
    return state.persistedReducer.global?.mprolabContracts?.usdcContract ?? []
  })
  return useMemo(() => contracts.filter(({ isActive }) => isActive), [contracts])
}

export function useClassicStakeContractAddress(): MprolabContract[] {
  return useSelector<AppState, MprolabContract[]>((state) => {
    return state.persistedReducer.global?.mprolabContracts?.classicStakeContract ?? []
  })
}

/** The fee vault(s) registered in the contracts config — one per licence collection. */
export function useFeeVaultContractAddress(): MprolabContract[] {
  return useSelector<AppState, MprolabContract[]>((state) => {
    return state.persistedReducer.global?.mprolabContracts?.feeVaultContract ?? []
  })
}

/** The swap router(s) registered in the contracts config — the page routes through the one on Base. */
export function useSwapRouterContractAddress(): MprolabContract[] {
  return useSelector<AppState, MprolabContract[]>((state) => {
    return state.persistedReducer.global?.mprolabContracts?.swapRouterContract ?? []
  })
}

export function useClassicDoubleStakeContractAddress(): MprolabContract[] {
  return useSelector<AppState, MprolabContract[]>((state) => {
    return state.persistedReducer.global?.mprolabContracts?.classicDoubleStakeContract ?? []
  })
}

export function useRewardProxyContractAddress(): MprolabContract[] {
  const contracts = useSelector<AppState, MprolabContract[]>((state) => {
    return state.persistedReducer.global?.mprolabContracts?.mproRewardProxyContract ?? []
  })
  return useMemo(() => contracts.filter(({ isActive }) => isActive), [contracts])
}

export function useMgaRegularNodesContractDetails(): MprolabContract[] {
  const contracts = useSelector<AppState, MprolabContract[]>((state) => {
    return state.persistedReducer.global?.mprolabContracts?.mgaRegularNodesContract ?? []
  })
  return useMemo(() => contracts.filter(({ isActive }) => isActive), [contracts])
}

export function useMgaLightNodesContractDetails(): MprolabContract[] {
  const contracts = useSelector<AppState, MprolabContract[]>((state) => {
    return state.persistedReducer.global?.mprolabContracts?.lightNodesContract ?? []
  })
  return useMemo(() => contracts.filter(({ isActive }) => isActive), [contracts])
}

export function useRegularNodesContractDetails(): MprolabContract[] {
  const contracts = useSelector<AppState, MprolabContract[]>((state) => {
    return state.persistedReducer.global?.mprolabContracts?.regularNodesContract ?? []
  })
  return useMemo(() => contracts.filter(({ isActive }) => isActive), [contracts])
}
